import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Tab,
  Tabs,
  ThemeProvider,
  createTheme,
  Paper,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  TextField,
  InputAdornment,
  Checkbox,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useIniciativasWithCache } from '../hooks/useIniciativasWithCache';
import TablaOperaciones from './TablaOperaciones';
import KanbanOperaciones from './KanbanOperaciones';
import './Operaciones.css';

const Operaciones = () => {
  // Funciones para manejar localStorage con migración
  const getFromLocalStorage = (key, defaultValue) => {
    try {
      const item = localStorage.getItem(`operaciones_${key}`);
      if (!item) return defaultValue;

      const parsed = JSON.parse(item);

      // Migración especial para añosSeleccionados
      if (key === 'añosSeleccionados') {
        // Si es un número (formato anterior), convertir a array
        if (typeof parsed === 'number') {
          return [parsed];
        }
        // Si es un array, mantener como está
        if (Array.isArray(parsed)) {
          return parsed;
        }
        // Si no es válido, usar 2026 por defecto
        return [2026];
      }

      // Migración para filtros que ahora son arrays múltiples
      if (['estadosFiltro', 'areasFiltro', 'areasEjecucionFiltro', 'jefesFiltro', 'lineaNegocioFiltro', 'negocioFiltro', 'negociosFiltro', 'tipoInnovacionFiltro', 'tipoFiltro', 'madurezFiltro'].includes(key)) {
        // Si es un string vacío (formato anterior), convertir a array vacío (todos seleccionados)
        if (parsed === '') {
          return [];
        }
        // Si es un string no vacío, convertir a array de un elemento
        if (typeof parsed === 'string') {
          return [parsed];
        }
        // Si es un array, mantener como está
        if (Array.isArray(parsed)) {
          return parsed;
        }
        // Si no es válido, usar array vacío (todos seleccionados)
        return [];
      }

      return parsed;
    } catch (error) {
      console.error(`Error cargando ${key} desde localStorage:`, error);
      return defaultValue;
    }
  };

  const saveToLocalStorage = (key, value) => {
    try {
      localStorage.setItem(`operaciones_${key}`, JSON.stringify(value));
    } catch (error) {
      console.error(`Error guardando ${key} en localStorage:`, error);
    }
  };

  // Estados con valores desde localStorage
  const [activeTab, setActiveTab] = useState(() => getFromLocalStorage('activeTab', 0));
  const [añosSeleccionados, setAñosSeleccionados] = useState(() => getFromLocalStorage('añosSeleccionados', [])); // [] significa "todos los años"

  // Estados para los filtros
  const [searchTerm, setSearchTerm] = useState(() => getFromLocalStorage('searchTerm', ''));
  const [estadosFiltro, setEstadosFiltro] = useState(() => getFromLocalStorage('estadosFiltro', []));
  const [areasFiltro, setAreasFiltro] = useState(() => getFromLocalStorage('areasFiltro', []));
  const [areasEjecucionFiltro, setAreasEjecucionFiltro] = useState(() => getFromLocalStorage('areasEjecucionFiltro', []));
  const [jefesFiltro, setJefesFiltro] = useState(() => getFromLocalStorage('jefesFiltro', []));
  const [lineaNegocioFiltro, setLineaNegocioFiltro] = useState(() => getFromLocalStorage('lineaNegocioFiltro', []));
  const [negocioFiltro, setNegocioFiltro] = useState(() => getFromLocalStorage('negocioFiltro', []));
  const [negociosFiltro, setNegociosFiltro] = useState(() => getFromLocalStorage('negociosFiltro', []));
  // Nuevos filtros de innovación
  const [innovacionFiltro, setInnovacionFiltro] = useState(() => {
    const saved = getFromLocalStorage('innovacionFiltro', []);
    // Si hay un valor string del formato anterior, convertirlo a array
    if (typeof saved === 'string') {
      if (saved === 'todos') return [];
      if (saved === 'innovacion') return ['innovacion'];
      if (saved === 'no_innovacion') return ['no_innovacion'];
      return [];
    }
    // Si hay un valor booleano del formato anterior, convertirlo
    if (typeof saved === 'boolean') {
      return saved ? ['innovacion'] : [];
    }
    // Si es un array, validar que contenga valores válidos
    if (Array.isArray(saved)) {
      return saved.filter(v => ['innovacion', 'no_innovacion'].includes(v));
    }
    return [];
  });
  const [tipoInnovacionFiltro, setTipoInnovacionFiltro] = useState(() => {
    const saved = getFromLocalStorage('tipoInnovacionFiltro', []);
    // Si hay un string del formato anterior, convertirlo a array
    if (typeof saved === 'string') {
      if (saved === 'todos') return [];
      if (['innocard', 'innotrans', 'sin_especificar'].includes(saved)) return [saved];
      return [];
    }
    // Si es un array, validar que contenga valores válidos
    if (Array.isArray(saved)) {
      return saved.filter(v => ['innocard', 'innotrans', 'sin_especificar'].includes(v));
    }
    return [];
  });
  const [tipoFiltro, setTipoFiltro] = useState(() => getFromLocalStorage('tipoFiltro', []));
  const [madurezFiltro, setMadurezFiltro] = useState(() => getFromLocalStorage('madurezFiltro', []));
  const [desvioFiltro, setDesvioFiltro] = useState(() => getFromLocalStorage('desvioFiltro', []));


  // Hook personalizado para manejar iniciativas con caché
  const {
    iniciativas,
    loading: loadingIniciativas,
    error: errorIniciativas,
    refreshData,
    getCacheStats,
    hasValidCache,
    isCacheExpired
  } = useIniciativasWithCache({
    autoLoad: true,
    estadosFiltro: ['aprobado', 'planificada', 'en_curso', 'bloqueada', 'cancelada', 'finalizada'],
    endpoint: 'operaciones'
  });

  const navigate = useNavigate();
  const location = useLocation();

  // Funciones wrapper para actualizar estado y localStorage
  const updateActiveTab = (value) => {
    setActiveTab(value);
    saveToLocalStorage('activeTab', value);
  };

  const updateAñosSeleccionados = (value) => {
    setAñosSeleccionados(value);
    saveToLocalStorage('añosSeleccionados', value);
  };

  const updateSearchTerm = (value) => {
    setSearchTerm(value);
    saveToLocalStorage('searchTerm', value);
  };

  const updateEstadosFiltro = (value) => {
    setEstadosFiltro(value);
    saveToLocalStorage('estadosFiltro', value);
  };

  const updateAreasFiltro = (value) => {
    setAreasFiltro(value);
    saveToLocalStorage('areasFiltro', value);
  };

  const updateAreasEjecucionFiltro = (value) => {
    setAreasEjecucionFiltro(value);
    saveToLocalStorage('areasEjecucionFiltro', value);
  };

  const updateJefesFiltro = (value) => {
    setJefesFiltro(value);
    saveToLocalStorage('jefesFiltro', value);
  };

  const updateLineaNegocioFiltro = (value) => {
    setLineaNegocioFiltro(value);
    saveToLocalStorage('lineaNegocioFiltro', value);
  };

  const updateNegocioFiltro = (value) => {
    setNegocioFiltro(value);
    saveToLocalStorage('negocioFiltro', value);
  };

  const updateNegociosFiltro = (value) => {
    setNegociosFiltro(value);
    saveToLocalStorage('negociosFiltro', value);
  };

  const updateInnovacionFiltro = (value) => {
    setInnovacionFiltro(value);
    saveToLocalStorage('innovacionFiltro', value);
  };

  const updateTipoInnovacionFiltro = (value) => {
    setTipoInnovacionFiltro(value);
    saveToLocalStorage('tipoInnovacionFiltro', value);
  };

  const updateTipoFiltro = (value) => {
    setTipoFiltro(value);
    saveToLocalStorage('tipoFiltro', value);
  };

  const updateMadurezFiltro = (value) => {
    setMadurezFiltro(value);
    saveToLocalStorage('madurezFiltro', value);
  };

  const updateDesvioFiltro = (value) => {
    setDesvioFiltro(value);
    saveToLocalStorage('desvioFiltro', value);
  };

  // Funciones para obtener el display formateado de los valores
  const getEstadoDisplay = useCallback((estado) => {
    const estadoMap = {
      'oportunidad_identificada': 'Oportunidad identificada',
      'en_preparacion_propuesta': 'En preparación propuesta',
      'supervision': 'Supervisión',
      'entregada': 'Entregada',
      'rechazada': 'Rechazada',
      'stand_by': 'Stand-By',
      'aprobado': 'Aprobado',
      'planificada': 'Planificada',
      'en_curso': 'En curso',
      'bloqueada': 'Bloqueada',
      'cancelada': 'Cancelada',
      'finalizada': 'Finalizada',
      'null': 'Sin estado'
    };
    return estadoMap[estado] || estado;
  }, []);

  const getTipoDisplay = (tipo) => {
    const tipoMap = {
      'iniciativa': 'Iniciativa',
      'servicio': 'Servicio',
      'generica': 'Genérica',
      'mantenimiento': 'Mantenimiento',
      'programa': 'Programa',
      'null': 'Sin especificar'
    };
    return tipoMap[tipo] || tipo;
  };

  const getMadurezDisplay = (madurez) => {
    const madurezMap = {
      'poc': 'POC',
      'piloto': 'Piloto',
      'proyecto': 'Proyecto',
      'n/a': 'N/A',
      'null': 'Sin especificar'
    };
    return madurezMap[madurez] || madurez;
  };

  const getDesvioDisplay = (nivel) => {
    const desvioMap = {
      'sin_desvios': 'Sin desvíos',
      'medio': 'Desvío medio',
      'grave': 'Desvío grave'
    };
    return desvioMap[nivel] || nivel;
  };

  const getTipoInnovacionDisplay = (tipo) => {
    switch (tipo) {
      case 'innocard': return 'INNOCARD';
      case 'innotrans': return 'INNOTRANS';
      case 'sin_especificar': return 'Sin especificar';
      default: return tipo;
    }
  };

  // Funciones helper para manejar selección múltiple
  const isAllSelected = (selectedItems, allItems) => {
    return selectedItems.length === 0 || (allItems.length > 0 && selectedItems.length === allItems.length);
  };

  const handleSelectChange = (event, allItems, updateFunction, currentSelection) => {
    const value = event.target.value;
    
    // Si se hace clic en "all", alternar entre seleccionar todo y deseleccionar todo
    if (value[value.length - 1] === 'all') {
      // Si ya está todo seleccionado, desmarcar todos (usar '__NONE__')
      if (isAllSelected(currentSelection, allItems)) {
        updateFunction(['__NONE__']);
      } else {
        // Si no está todo seleccionado, seleccionar todos (array vacío)
        updateFunction([]);
      }
      return;
    }
    
    // Filtrar '__NONE__' del array de selecciones normales
    updateFunction(value.filter(v => v !== '__NONE__'));
  };

  const getRenderValue = (selectedItems, allItems, label) => {
    // Si contiene '__NONE__', mostrar "Ninguno"
    if (selectedItems.includes('__NONE__')) {
      return 'Ninguno';
    }
    
    if (selectedItems.length === 0) {
      return 'Todos';
    }
    
    if (isAllSelected(selectedItems, allItems)) {
      return 'Todos';
    }
    
    if (selectedItems.length === 1) {
      // Para estados, mostrar el display formateado
      if (label === 'estados') {
        return getEstadoDisplay(selectedItems[0]);
      }
      // Para tipos de innovación, usar el display apropiado
      if (label === 'tipos') {
        return getTipoInnovacionDisplay(selectedItems[0]);
      }
      // Para madurez, mostrar el display formateado
      if (label === 'madurez') {
        return getMadurezDisplay(selectedItems[0]);
      }
      // Para tipo de iniciativa, mostrar el display formateado
      if (label === 'tipo') {
        return getTipoDisplay(selectedItems[0]);
      }
      return selectedItems[0];
    }
    
    return `${selectedItems.length} seleccionado${selectedItems.length > 1 ? 's' : ''}`;
  };

  // Función para limpiar todos los filtros
  const limpiarFiltros = () => {
    updateAñosSeleccionados([]); // [] significa "todos los años"
    updateSearchTerm('');
    updateEstadosFiltro([]);
    updateAreasFiltro([]);
    updateAreasEjecucionFiltro([]);
    updateJefesFiltro([]);
    updateLineaNegocioFiltro([]);
    updateNegocioFiltro([]);
    updateNegociosFiltro([]);
    updateInnovacionFiltro([]);
    updateTipoInnovacionFiltro([]);
    updateTipoFiltro([]);
    updateMadurezFiltro([]);
    updateDesvioFiltro([]);
  };

  // Leer parámetros desde la URL al cargar el componente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Si hay parámetros de navegación desde otra página, se aplican siempre
    // (sobrescriben localStorage para reflejar la intención del enlace)
    const hasNavParams = params.has('estados') || params.has('tipos') || params.has('desvio');

    // Restaurar tab activo
    const tabParam = params.get('tab');
    if (tabParam) {
      updateActiveTab(parseInt(tabParam));
    }

    const searchParam = params.get('search');
    if (searchParam && !searchTerm) {
      updateSearchTerm(searchParam);
    }

    const estadosParam = params.get('estados');
    if (estadosParam && (hasNavParams || estadosFiltro.length === 0)) {
      updateEstadosFiltro(estadosParam.split(',').filter(Boolean));
    } else if (hasNavParams && !estadosParam) {
      updateEstadosFiltro([]);
    }

    const areasParam = params.get('areas');
    if (areasParam && areasFiltro.length === 0) {
      updateAreasFiltro(areasParam.split(',').filter(Boolean));
    }

    const jefesParam = params.get('jefes');
    if (jefesParam && jefesFiltro.length === 0) {
      updateJefesFiltro(jefesParam.split(',').filter(Boolean));
    }

    const lineaNegocioParam = params.get('lineaNegocio');
    if (lineaNegocioParam && lineaNegocioFiltro.length === 0) {
      updateLineaNegocioFiltro(lineaNegocioParam.split(',').filter(Boolean));
    }

    const negocioParam = params.get('negocio');
    if (negocioParam && negocioFiltro.length === 0) {
      updateNegocioFiltro(negocioParam.split(',').filter(Boolean));
    }

    const negociosParam = params.get('negocios');
    if (negociosParam && negociosFiltro.length === 0) {
      updateNegociosFiltro(negociosParam.split(',').filter(Boolean));
    }

    const innovacionParam = params.get('innovacion');
    if (innovacionParam && innovacionFiltro.length === 0) {
      updateInnovacionFiltro(innovacionParam.split(',').filter(Boolean));
    }

    const tipoInnovacionParam = params.get('tipoInnovacion');
    if (tipoInnovacionParam && (hasNavParams || tipoInnovacionFiltro.length === 0)) {
      updateTipoInnovacionFiltro(tipoInnovacionParam.split(',').filter(Boolean));
    }

    const tiposParam = params.get('tipos');
    if (tiposParam && (hasNavParams || tipoFiltro.length === 0)) {
      updateTipoFiltro(tiposParam.split(',').filter(Boolean));
    } else if (hasNavParams && !tiposParam) {
      updateTipoFiltro([]);
    }

    const desvioParam = params.get('desvio');
    if (desvioParam && (hasNavParams || desvioFiltro.length === 0)) {
      updateDesvioFiltro(desvioParam.split(',').filter(Boolean));
    } else if (hasNavParams && !desvioParam) {
      updateDesvioFiltro([]);
    }

    const añosParam = params.get('años');
    if (añosParam) {
      const añosArray = añosParam.split(',').map(Number).filter(Boolean);
      if (añosArray.length > 0) {
        updateAñosSeleccionados(añosArray);
      }
    }
  }, [location.search]);

  // Función para forzar recarga de datos
  const recargarDatos = useCallback(async () => {
    console.log('Forzando recarga de datos...');
    const result = await refreshData();
    if (result.success) {
      console.log(`Datos actualizados: ${result.message}`);
    } else {
      console.error(`Error al recargar: ${result.message}`);
    }
  }, [refreshData]);

  // Función para obtener el año de la iniciativa (debe estar definida antes del useEffect)
  const getAñoIniciativa = useCallback((iniciativa) => {
    // Priorizar el campo año si está disponible
    if (iniciativa.año) {
      return Number(iniciativa.año);
    }
    // Fallback a las fechas existentes
    if (iniciativa.fecha_de_inicio_real) {
      return new Date(iniciativa.fecha_de_inicio_real).getFullYear();
    } else if (iniciativa.fecha_de_inicio_planificada) {
      return new Date(iniciativa.fecha_de_inicio_planificada).getFullYear();
    } else if (iniciativa.fecha_de_aprobacion_propuesta_firmada) {
      return new Date(iniciativa.fecha_de_aprobacion_propuesta_firmada).getFullYear();
    } else if (iniciativa.fecha_creacion) {
      return new Date(iniciativa.fecha_creacion).getFullYear();
    }
    return null;
  }, []);

  // Función para filtrar iniciativas basándose en los filtros aplicados (excluyendo el filtro actual)
  // El filtro de año SIEMPRE se aplica como filtro padre
  // Los demás filtros solo se aplican cuando no se está calculando opciones únicas (excludeFilter = null)
  const getIniciativasFiltradas = useCallback((excludeFilter = null) => {
    return iniciativas.filter(iniciativa => {
      // Filtrar iniciativas de tipo genérica (SIEMPRE)
      if (iniciativa.tipo_de_iniciativa === 'generica') return false;
      
      // Filtro por años (SIEMPRE aplicado - es el filtro padre)
      // Si añosSeleccionados está vacío, mostrar todos los años
      if (añosSeleccionados.includes('__NONE__')) {
        return false;
      }
      if (añosSeleccionados.length > 0 && !añosSeleccionados.includes('__NONE__')) {
        const year = getAñoIniciativa(iniciativa);
        if (!añosSeleccionados.includes(year)) return false;
      }

      // Si estamos calculando opciones únicas para un dropdown,
      // NO aplicar los demás filtros (solo año y búsqueda de texto)
      if (excludeFilter !== null) {
        // Filtro de texto abierto (aplicado siempre)
        if (searchTerm && searchTerm.trim() !== '') {
          const lower = searchTerm.toLowerCase();
          const matches = (
            (iniciativa.nombre_de_la_oportunidad && iniciativa.nombre_de_la_oportunidad.toLowerCase().includes(lower)) ||
            (iniciativa.negocio_estructura?.negocio && iniciativa.negocio_estructura.negocio.toLowerCase().includes(lower)) ||
            (iniciativa.negocio_estructura?.linea_de_negocio && iniciativa.negocio_estructura.linea_de_negocio.toLowerCase().includes(lower)) ||
            (iniciativa.negocio_estructura?.subnegocio && iniciativa.negocio_estructura.subnegocio.toLowerCase().includes(lower)) ||
            (iniciativa.tecnologia_nombre && iniciativa.tecnologia_nombre.toLowerCase().includes(lower)) ||
            (iniciativa.area_ejecucion_nombre && iniciativa.area_ejecucion_nombre.toLowerCase().includes(lower)) ||
            (iniciativa.jefe_proyecto_nombre && iniciativa.jefe_proyecto_nombre.toLowerCase().includes(lower)) ||
            (iniciativa.estado && getEstadoDisplay(iniciativa.estado).toLowerCase().includes(lower))
          );
          if (!matches) return false;
        }
        // No aplicar otros filtros cuando calculamos opciones únicas
        return true;
      }

      // Cuando NO estamos calculando opciones (excludeFilter = null),
      // aplicar TODOS los filtros para filtrar las iniciativas mostradas
      
      // Filtro de texto abierto
      if (searchTerm && searchTerm.trim() !== '') {
        const lower = searchTerm.toLowerCase();
        const matches = (
          (iniciativa.nombre_de_la_oportunidad && iniciativa.nombre_de_la_oportunidad.toLowerCase().includes(lower)) ||
          (iniciativa.negocio_estructura?.negocio && iniciativa.negocio_estructura.negocio.toLowerCase().includes(lower)) ||
          (iniciativa.negocio_estructura?.linea_de_negocio && iniciativa.negocio_estructura.linea_de_negocio.toLowerCase().includes(lower)) ||
          (iniciativa.negocio_estructura?.subnegocio && iniciativa.negocio_estructura.subnegocio.toLowerCase().includes(lower)) ||
          (iniciativa.tecnologia_nombre && iniciativa.tecnologia_nombre.toLowerCase().includes(lower)) ||
          (iniciativa.area_ejecucion_nombre && iniciativa.area_ejecucion_nombre.toLowerCase().includes(lower)) ||
          (iniciativa.jefe_proyecto_nombre && iniciativa.jefe_proyecto_nombre.toLowerCase().includes(lower)) ||
          (iniciativa.estado && getEstadoDisplay(iniciativa.estado).toLowerCase().includes(lower))
        );
        if (!matches) return false;
      }

      // Filtro por estado
      if (estadosFiltro.includes('__NONE__') && excludeFilter !== 'estado') {
        return false;
      }
      if (estadosFiltro.length > 0 && !estadosFiltro.includes('__NONE__')) {
        const estadoComparar = iniciativa.estado || 'null';
        if (!estadosFiltro.includes(estadoComparar)) return false;
      }

      // Filtro por área
      if (areasFiltro.includes('__NONE__') && excludeFilter !== 'area') {
        return false;
      }
      if (areasFiltro.length > 0 && !areasFiltro.includes('__NONE__')) {
        const areaComparar = iniciativa.tecnologia_nombre || 'null';
        if (!areasFiltro.includes(areaComparar)) return false;
      }

      // Filtro por área de ejecución
      if (areasEjecucionFiltro.includes('__NONE__') && excludeFilter !== 'areaEjecucion') {
        return false;
      }
      if (areasEjecucionFiltro.length > 0 && !areasEjecucionFiltro.includes('__NONE__')) {
        const areaEjecucionComparar = iniciativa.area_ejecucion_nombre || 'null';
        if (!areasEjecucionFiltro.includes(areaEjecucionComparar)) return false;
      }

      // Filtro por jefe
      if (jefesFiltro.includes('__NONE__') && excludeFilter !== 'jefe') {
        return false;
      }
      if (jefesFiltro.length > 0 && !jefesFiltro.includes('__NONE__')) {
        const jefeComparar = iniciativa.jefe_proyecto_nombre || 'null';
        if (!jefesFiltro.includes(jefeComparar)) return false;
      }

      // Filtros de jerarquía de negocio - aplicar en cascada respetando la jerarquía
      let lineaNegocio = iniciativa.negocio_estructura?.linea_de_negocio || iniciativa.linea_de_negocio || iniciativa.negocio_estructura_linea_de_negocio;
      if (!lineaNegocio && typeof iniciativa.negocio_estructura === 'string') {
        lineaNegocio = iniciativa.negocio_estructura.split('>')[0].trim();
      }
      const negocio = iniciativa.negocio_estructura?.negocio || iniciativa.negocio || iniciativa.negocio_estructura_negocio;
      const subnegocio = iniciativa.negocio_estructura_nombre;

      if (lineaNegocioFiltro.includes('__NONE__') && excludeFilter !== 'lineaNegocio') {
        return false;
      }
      if (lineaNegocioFiltro.length > 0 && !lineaNegocioFiltro.includes('__NONE__')) {
        let lnCompare = lineaNegocio;
        if (typeof lnCompare === 'string' && lnCompare.includes('>')) {
          lnCompare = lnCompare.split('>')[0].trim();
        }
        const lineaNegocioComparar = lnCompare || 'null';
        if (!lineaNegocioFiltro.includes(lineaNegocioComparar)) return false;
      }
      if (negocioFiltro.includes('__NONE__') && excludeFilter !== 'negocio') {
        return false;
      }
      if (negocioFiltro.length > 0 && !negocioFiltro.includes('__NONE__')) {
        const negocioComparar = negocio || 'null';
        if (!negocioFiltro.includes(negocioComparar)) return false;
      }
      if (negociosFiltro.includes('__NONE__') && excludeFilter !== 'subnegocio') {
        return false;
      }
      if (negociosFiltro.length > 0 && !negociosFiltro.includes('__NONE__')) {
        const subNegocioComparar = subnegocio || 'null';
        if (!negociosFiltro.includes(subNegocioComparar)) return false;
      }

      // Filtros de innovación
      if (innovacionFiltro.includes('__NONE__') && excludeFilter !== 'innovacion') {
        return false;
      }
      if (innovacionFiltro.length > 0 && !innovacionFiltro.includes('__NONE__')) {
        const esInnovacion = iniciativa.innovacion;
        const cumpleFiltro = innovacionFiltro.some(filtro => {
          if (filtro === 'innovacion') return esInnovacion;
          if (filtro === 'no_innovacion') return !esInnovacion;
          return false;
        });
        if (!cumpleFiltro) return false;
      }

      // Filtro por tipo de innovación
      if (tipoInnovacionFiltro.includes('__NONE__') && excludeFilter !== 'tipoInnovacion') {
        return false;
      }
      if (tipoInnovacionFiltro.length > 0 && !tipoInnovacionFiltro.includes('__NONE__')) {
        const tipoProyecto = iniciativa.tipo_innovacion;
        const cumpleFiltro = tipoInnovacionFiltro.some(filtro => {
          if (filtro === 'innocard') return tipoProyecto === 'innocard';
          if (filtro === 'innotrans') return tipoProyecto === 'innotrans';
          if (filtro === 'sin_especificar') return !tipoProyecto || tipoProyecto === '';
          return false;
        });
        if (!cumpleFiltro) return false;
      }

      // Filtro por tipo de iniciativa
      if (tipoFiltro.includes('__NONE__') && excludeFilter !== 'tipo') {
        return false;
      }
      if (tipoFiltro.length > 0 && !tipoFiltro.includes('__NONE__')) {
        const tipoComparar = iniciativa.tipo_de_iniciativa || 'null';
        if (!tipoFiltro.includes(tipoComparar)) return false;
      }

      // Filtro por madurez
      if (madurezFiltro.includes('__NONE__') && excludeFilter !== 'madurez') {
        return false;
      }
      if (madurezFiltro.length > 0 && !madurezFiltro.includes('__NONE__')) {
        if (iniciativa.tipo_de_iniciativa === 'iniciativa') {
          // Para iniciativas, comparar con el valor de madurez o null si no tiene
          const madurezComparar = iniciativa.madurez || 'null';
          if (!madurezFiltro.includes(madurezComparar)) return false;
        } else if (['servicio', 'mantenimiento', 'generica', 'programa'].includes(iniciativa.tipo_de_iniciativa)) {
          // Para otros tipos, solo mostrar si N/A está seleccionado
          if (!madurezFiltro.includes('n/a')) return false;
        }
      }

      // Filtro por desvío — misma lógica que getGradoDesvio en TablaOperaciones
      if (desvioFiltro.includes('__NONE__') && excludeFilter !== 'desvio') {
        return false;
      }
      if (desvioFiltro.length > 0 && !desvioFiltro.includes('__NONE__')) {
        // 1. Fecha fin vencida con proyecto en curso
        const fechaFinRaw = iniciativa.fecha_de_fin_real || iniciativa.fecha_de_fin_planificada;
        let fechaNivel = 0;
        if (fechaFinRaw && iniciativa.estado === 'en_curso') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const fechaFin = new Date(fechaFinRaw);
          fechaFin.setHours(0, 0, 0, 0);
          if (fechaFin < today) fechaNivel = 2;
          else if (fechaFin.getTime() === today.getTime()) fechaNivel = 1;
        }

        // 2. % s/C. Oferta (porcentaje_sobre_estimado >= 100)
        const porcentajeSobreOferta = iniciativa.porcentaje_sobre_estimado || '-';
        const ofertaNivel = (porcentajeSobreOferta !== '-' && porcentajeSobreOferta >= 100) ? 2 : 0;

        // 3. Desvío planificación y desvío coste real
        const desvioPlan = (iniciativa.desviacion_avance !== undefined && iniciativa.desviacion_avance !== null)
          ? iniciativa.desviacion_avance : null;
        const desvioCosteReal = (iniciativa.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real !== null &&
          iniciativa.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real !== undefined)
          ? iniciativa.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real : null;

        let desvioNivel = 0;
        if ((desvioPlan !== null && desvioPlan >= 20) || (desvioCosteReal !== null && desvioCosteReal >= 20)) desvioNivel = 2;
        else if ((desvioPlan !== null && desvioPlan >= 10) || (desvioCosteReal !== null && desvioCosteReal >= 10)) desvioNivel = 1;

        // 4. Nivel final: máxima severidad de los tres indicadores
        const nivelFinal = Math.max(fechaNivel, ofertaNivel, desvioNivel);
        const NIVELES_MAP = ['sin_desvios', 'medio', 'grave'];
        const nivelDesvio = NIVELES_MAP[nivelFinal];

        if (!desvioFiltro.includes(nivelDesvio)) return false;
      }

      return true;
    });
  }, [iniciativas, añosSeleccionados, estadosFiltro, areasFiltro, areasEjecucionFiltro, jefesFiltro, lineaNegocioFiltro, negocioFiltro, negociosFiltro, innovacionFiltro, tipoInnovacionFiltro, tipoFiltro, madurezFiltro, desvioFiltro, getAñoIniciativa, searchTerm, getEstadoDisplay]);

  // Funciones para obtener opciones únicas basándose en los demás filtros aplicados
  // Esto asegura que solo se muestren opciones que realmente tienen datos
  const estadosUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('estado');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      if (i.estado) {
        valores.add(i.estado);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort();
  }, [getIniciativasFiltradas]);

  const areasUnicas = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('area');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      if (i.tecnologia_nombre) {
        valores.add(i.tecnologia_nombre);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort((a, b) => {
      if (a === 'null') return 1;
      if (b === 'null') return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [getIniciativasFiltradas]);

  const areasEjecucionUnicas = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('areaEjecucion');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      if (i.area_ejecucion_nombre) {
        valores.add(i.area_ejecucion_nombre);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort((a, b) => {
      if (a === 'null') return 1;
      if (b === 'null') return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [getIniciativasFiltradas]);

  const jefesUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('jefe');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      if (i.jefe_proyecto_nombre) {
        valores.add(i.jefe_proyecto_nombre);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort((a, b) => {
      if (a === 'null') return 1;
      if (b === 'null') return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [getIniciativasFiltradas]);

  const lineasNegocioUnicas = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('lineaNegocio');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      let val = i.negocio_estructura?.linea_de_negocio || i.linea_de_negocio || i.negocio_estructura_linea_de_negocio;
      if (!val && typeof i.negocio_estructura === 'string') {
        val = i.negocio_estructura.split('>')[0].trim();
      }
      if (val) {
        valores.add(val);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort((a, b) => {
      if (a === 'null') return 1;
      if (b === 'null') return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [getIniciativasFiltradas]);

  const negociosUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('negocio');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      const val = i.negocio_estructura?.negocio ||
        i.negocio ||
        i.negocio_estructura_negocio ||
        (typeof i.negocio_estructura === 'string' ? null : i.negocio_estructura?.negocio);
      if (val) {
        valores.add(val);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort((a, b) => {
      if (a === 'null') return 1;
      if (b === 'null') return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [getIniciativasFiltradas]);

  const subNegociosUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('subnegocio');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      if (i.negocio_estructura_nombre) {
        valores.add(i.negocio_estructura_nombre);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort((a, b) => {
      if (a === 'null') return 1;
      if (b === 'null') return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [getIniciativasFiltradas]);

  // Opciones fijas para tipo de innovación
  const tiposInnovacionUnicos = ['innocard', 'innotrans', 'sin_especificar'];

  // Opciones únicas para tipo de iniciativa
  const tiposUnicos = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('tipo');
    const valores = new Set();
    iniciativasFiltradas.forEach(i => {
      if (i.tipo_de_iniciativa) {
        valores.add(i.tipo_de_iniciativa);
      } else {
        valores.add('null');
      }
    });
    return Array.from(valores).sort();
  }, [getIniciativasFiltradas]);

  // Opciones únicas para madurez (incluye N/A para tipos que no son iniciativa)
  const madurezsUnicas = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas('madurez');
    const valores = new Set();

    // Iniciativas de tipo 'iniciativa' con valores de madurez
    const iniciativasTipoIniciativa = iniciativasFiltradas.filter(i => i.tipo_de_iniciativa === 'iniciativa');
    let haySinMadurez = false;
    iniciativasTipoIniciativa.forEach(i => {
      if (i.madurez) {
        valores.add(i.madurez);
      } else {
        haySinMadurez = true;
      }
    });

    // Si hay iniciativas tipo iniciativa sin madurez, agregar null
    if (haySinMadurez) {
      valores.add('null');
    }

    // Si hay iniciativas de tipo servicio, mantenimiento o genérica, agregar N/A
    const hayOtrosTipos = iniciativasFiltradas.some(i =>
      ['servicio', 'mantenimiento', 'generica', 'programa'].includes(i.tipo_de_iniciativa)
    );
    if (hayOtrosTipos) {
      valores.add('n/a');
    }
    
    return Array.from(valores).sort();
  }, [getIniciativasFiltradas]);

  // Calcular total de iniciativas filtradas para mostrar en la pestaña
  const totalIniciativasFiltradas = useMemo(() => {
    return getIniciativasFiltradas().length;
  }, [getIniciativasFiltradas]);

  // Obtener iniciativas filtradas para pasar a los componentes
  const iniciativasFiltradas = useMemo(() => {
    return getIniciativasFiltradas();
  }, [getIniciativasFiltradas]);

  // KPIs calculados dinámicamente
  const kpis = useMemo(() => {
    const iniciativasFiltradas = getIniciativasFiltradas();
    const total = iniciativasFiltradas.length;

    if (total === 0) {
      return {
        total: 0,
        activos: 0,
        innovacion: 0,
        presupuesto: 0,
        finalizados: 0,
        desviados: 0,
        aprobados: 0,
      };
    }

    // Proyectos activos (en curso + planificadas)
    const activos = iniciativasFiltradas.filter(i =>
      ['planificada', 'en_curso'].includes(i.estado)
    ).length;

    const aprobados = iniciativasFiltradas.filter(i => i.estado === 'aprobado').length;
    console.log('Proyectos aprobados:', aprobados);

    // Proyectos de innovación
    const innovacion = iniciativasFiltradas.filter(i => i.innovacion).length;

    // Presupuesto total (suma de costes estimados de desarrollo o clientes)
    const presupuesto = iniciativasFiltradas.reduce((sum, i) => {
      const coste = parseFloat(i.coste_de_desarrollo_estimado) ||
        parseFloat(i.importe_total_estimado) ||
        parseFloat(i.importe_total_cliente) || 0;
      return sum + coste;
    }, 0);

    // Proyectos finalizados
    const finalizados = iniciativasFiltradas.filter(i => i.estado === 'finalizada').length;

    // Proyectos desviados: grave (nivelFinal=2) + medio (nivelFinal=1) — misma lógica que getGradoDesvio
    const desviados = iniciativasFiltradas.filter(i => {
      // 1. Fecha fin vencida con proyecto en curso
      const fechaFinRaw = i.fecha_de_fin_real || i.fecha_de_fin_planificada;
      let fechaNivel = 0;
      if (fechaFinRaw && i.estado === 'en_curso') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const fechaFin = new Date(fechaFinRaw);
        fechaFin.setHours(0, 0, 0, 0);
        if (fechaFin < today) fechaNivel = 2;
        else if (fechaFin.getTime() === today.getTime()) fechaNivel = 1;
      }
      // 2. % s/C. Oferta
      const porcentajeSobreOferta = i.porcentaje_sobre_estimado || '-';
      const ofertaNivel = (porcentajeSobreOferta !== '-' && porcentajeSobreOferta >= 100) ? 2 : 0;
      // 3. Desvío planificación y desvío coste real
      const desvioPlan = (i.desviacion_avance !== undefined && i.desviacion_avance !== null) ? i.desviacion_avance : null;
      const desvioCosteReal = (i.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real !== null &&
        i.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real !== undefined)
        ? i.desviacion_coste_real_sobre_coste_estimado_menos_porcentaje_real : null;
      let desvioNivel = 0;
      if ((desvioPlan !== null && desvioPlan >= 20) || (desvioCosteReal !== null && desvioCosteReal >= 20)) desvioNivel = 2;
      else if ((desvioPlan !== null && desvioPlan >= 10) || (desvioCosteReal !== null && desvioCosteReal >= 10)) desvioNivel = 1;
      // 4. Nivel final: grave o medio
      return Math.max(fechaNivel, ofertaNivel, desvioNivel) >= 1;
    }).length;

    return {
      total,
      activos,
      innovacion,
      presupuesto,
      finalizados,
      desviados,
      aprobados
    };
  }, [getIniciativasFiltradas]);

  // Función helper para formatear moneda
  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 1, useGrouping: true }).format(amount / 1000000)}M€`;
    } else if (amount >= 1000) {
      return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0, useGrouping: true }).format(amount / 1000)}k€`;
    }
    return `${new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0, useGrouping: true }).format(amount)}€`;
  };

  // Función para limpiar filtros que ya no son válidos cuando cambian los datos
  const limpiarFiltrosInvalidos = useCallback(() => {
    // Solo limpiar si hay datos cargados
    if (iniciativas.length === 0) return;

    const updates = {};

    if (estadosFiltro.length > 0) {
      const validEstados = estadosFiltro.filter(estado => estadosUnicos.includes(estado));
      if (validEstados.length !== estadosFiltro.length) {
        updates.estados = validEstados;
      }
    }

    if (areasFiltro.length > 0) {
      const validAreas = areasFiltro.filter(area => areasUnicas.includes(area));
      if (validAreas.length !== areasFiltro.length) {
        updates.areas = validAreas;
      }
    }

    if (areasEjecucionFiltro.length > 0) {
      const validAreasEjecucion = areasEjecucionFiltro.filter(area => areasEjecucionUnicas.includes(area));
      if (validAreasEjecucion.length !== areasEjecucionFiltro.length) {
        updates.areasEjecucion = validAreasEjecucion;
      }
    }

    if (jefesFiltro.length > 0) {
      const validJefes = jefesFiltro.filter(jefe => jefesUnicos.includes(jefe));
      if (validJefes.length !== jefesFiltro.length) {
        updates.jefes = validJefes;
      }
    }

    if (lineaNegocioFiltro.length > 0) {
      const validLineas = lineaNegocioFiltro.filter(linea => lineasNegocioUnicas.includes(linea));
      if (validLineas.length !== lineaNegocioFiltro.length) {
        updates.lineas = validLineas;
      }
    }

    if (negocioFiltro.length > 0) {
      const validNegocios = negocioFiltro.filter(negocio => negociosUnicos.includes(negocio));
      if (validNegocios.length !== negocioFiltro.length) {
        updates.negocios = validNegocios;
      }
    }

    if (negociosFiltro.length > 0) {
      const validSubNegocios = negociosFiltro.filter(subnegocio => subNegociosUnicos.includes(subnegocio));
      if (validSubNegocios.length !== negociosFiltro.length) {
        updates.subNegocios = validSubNegocios;
      }
    }

    // Aplicar todas las actualizaciones de una vez
    if (Object.keys(updates).length > 0) {
      if (updates.estados) updateEstadosFiltro(updates.estados);
      if (updates.areas) updateAreasFiltro(updates.areas);
      if (updates.areasEjecucion) updateAreasEjecucionFiltro(updates.areasEjecucion);
      if (updates.jefes) updateJefesFiltro(updates.jefes);
      if (updates.lineas) updateLineaNegocioFiltro(updates.lineas);
      if (updates.negocios) updateNegocioFiltro(updates.negocios);
      if (updates.subNegocios) updateNegociosFiltro(updates.subNegocios);
    }
  }, [iniciativas, estadosFiltro, areasFiltro, areasEjecucionFiltro, jefesFiltro, lineaNegocioFiltro, negocioFiltro, negociosFiltro, estadosUnicos, areasUnicas, areasEjecucionUnicas, jefesUnicos, lineasNegocioUnicas, negociosUnicos, subNegociosUnicos, updateEstadosFiltro, updateAreasFiltro, updateAreasEjecucionFiltro, updateJefesFiltro, updateLineaNegocioFiltro, updateNegocioFiltro, updateNegociosFiltro]);

  // Solo ejecutar la limpieza una vez después de cargar los datos
  useEffect(() => {
    if (iniciativas.length > 0) {
      limpiarFiltrosInvalidos();
    }
  }, [iniciativas.length]); // Solo cuando cambie la cantidad de iniciativas


  // Generar años disponibles basándose en las fechas reales de las iniciativas
  const añosDisponibles = useMemo(() => {
    if (iniciativas.length === 0) {
      // Fallback: si no hay iniciativas cargadas, usar el año actual y algunos anteriores
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 5; i++) {
        years.push(currentYear - i);
      }
      return years;
    }

    // Extraer años únicos de todas las iniciativas
    const yearsSet = new Set();
    iniciativas.forEach(iniciativa => {
      const year = getAñoIniciativa(iniciativa);
      if (year) {
        yearsSet.add(year);
      }
    });

    // Convertir a array y ordenar (más reciente primero)
    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [iniciativas, getAñoIniciativa]);

  // Tema personalizado para integración con Bootstrap
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0d6efd', // Bootstrap primary color
      },
      secondary: {
        main: '#6c757d', // Bootstrap secondary color
      },
      success: {
        main: '#198754', // Bootstrap success color
      },
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            minWidth: 120,
            '&.Mui-selected': {
              color: '#0d6efd',
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: '#0d6efd',
            height: 3,
          },
        },
      },
    },
  });

  const handleTabChange = (event, newValue) => {
    updateActiveTab(newValue);
  };

  const TabPanel = ({ children, value, index, ...other }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`operaciones-tabpanel-${index}`}
        aria-labelledby={`operaciones-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Fade in={true} timeout={300}>
            <Box>
              {children}
            </Box>
          </Fade>
        )}
      </div>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        px: { xs: 1, sm: 2, md: 3 },
        py: 2
      }}>

        {/* Navegación por Tabs */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: 0 }}>
            {/* KPIs Dashboard - Compacto */}
            <Box sx={{
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
              gap: 1.5
            }}>
              {/* Total Proyectos */}
              <Paper sx={{
                p: 1.5,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#0d6efd', mb: 0.5 }}>
                  {kpis.total}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Total Proyectos
                </Typography>
              </Paper>

              {/* Proyectos aprobados */}
              <Paper sx={{
                p: 1.5,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#20c997', mb: 0.5 }}>
                  {kpis.aprobados}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Aprobados
                </Typography>
              </Paper>

              {/* Proyectos Activos */}
              <Paper sx={{
                p: 1.5,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#198754', mb: 0.5 }}>
                  {kpis.activos}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  En Curso/Planif.
                </Typography>
              </Paper>

              {/* Finalizados */}
              <Paper sx={{
                p: 1.5,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#6f42c1', mb: 0.5 }}>
                  {kpis.total > 0 ? kpis.finalizados : 0}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Finalizados
                </Typography>
              </Paper>

              {/* desviados */}
              <Paper sx={{
                p: 1.5,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#dc3545', mb: 0.5 }}>
                  {kpis.desviados}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Desviados
                </Typography>
              </Paper>

              {/* Innovación */}
              <Paper sx={{
                p: 1.5,
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '8px'
              }}>
                <Typography variant="h6" sx={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#fd7e14', mb: 0.5 }}>
                  {kpis.total > 0 ? kpis.innovacion : 0}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Innovación
                </Typography>
              </Paper>

            </Box>

            {/* Filtros comunes para ambos componentes */}
            <Box sx={{ mt: '2rem', mb: '1rem', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>

              <TextField
                variant="outlined"
                placeholder={`Buscar proyectos...`}
                value={searchTerm}
                onChange={(e) => updateSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <i className="fas fa-search"></i>
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ minWidth: 200, flex: 1, maxWidth: { xs: '100%', md: 300 } }}
              />


              {/* Filtro de Años (múltiple) */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Año</InputLabel>
                <Select
                  multiple
                  value={añosSeleccionados}
                  label="Año"
                  onChange={(e) => handleSelectChange(e, añosDisponibles, updateAñosSeleccionados, añosSeleccionados)}
                  renderValue={() => getRenderValue(añosSeleccionados, añosDisponibles, 'años')}
                  sx={{
                    fontSize: '0.85rem',
                    '& .MuiSelect-select': {
                      padding: '8px 12px'
                    }
                  }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(añosSeleccionados, añosDisponibles)}
                        indeterminate={añosSeleccionados.length > 0 && añosSeleccionados.length < añosDisponibles.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {añosDisponibles.map((año) => (
                    <MenuItem key={año || 'null'} value={año} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={añosSeleccionados.length === 0 || añosSeleccionados.includes(año)} />
                      </ListItemIcon>
                      <ListItemText primary={año === null ? 'Sin especificar' : año} />
                      {año === new Date().getFullYear() && (
                        <Chip 
                          label="Actual"
                          size="small" 
                          sx={{ ml: 'auto', fontSize: '0.6rem', height: '16px' }}
                        />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Estado</InputLabel>
                <Select
                  multiple
                  value={estadosFiltro}
                  label="Estado"
                  onChange={(e) => handleSelectChange(e, estadosUnicos, updateEstadosFiltro, estadosFiltro)}
                  renderValue={() => getRenderValue(estadosFiltro, estadosUnicos, 'estados')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(estadosFiltro, estadosUnicos)}
                        indeterminate={estadosFiltro.length > 0 && estadosFiltro.length < estadosUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {estadosUnicos.map(estado => (
                    <MenuItem key={estado} value={estado} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={estadosFiltro.length === 0 || estadosFiltro.includes(estado)} />
                      </ListItemIcon>
                      <ListItemText primary={getEstadoDisplay(estado)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Área conocimiento</InputLabel>
                <Select
                  multiple
                  value={areasFiltro}
                  label="Área conocimiento"
                  onChange={(e) => handleSelectChange(e, areasUnicas, updateAreasFiltro, areasFiltro)}
                  renderValue={() => getRenderValue(areasFiltro, areasUnicas, 'áreas')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(areasFiltro, areasUnicas)}
                        indeterminate={areasFiltro.length > 0 && areasFiltro.length < areasUnicas.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todas" />
                  </MenuItem>
                  {areasUnicas.map(area => (
                    <MenuItem key={area} value={area} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={areasFiltro.length === 0 || areasFiltro.includes(area)} />
                      </ListItemIcon>
                      <ListItemText primary={area} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Área ejecución</InputLabel>
                <Select
                  multiple
                  value={areasEjecucionFiltro}
                  label="Área ejecución"
                  onChange={(e) => handleSelectChange(e, areasEjecucionUnicas, updateAreasEjecucionFiltro, areasEjecucionFiltro)}
                  renderValue={() => getRenderValue(areasEjecucionFiltro, areasEjecucionUnicas, 'áreas ejecución')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(areasEjecucionFiltro, areasEjecucionUnicas)}
                        indeterminate={areasEjecucionFiltro.length > 0 && areasEjecucionFiltro.length < areasEjecucionUnicas.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todas" />
                  </MenuItem>
                  {areasEjecucionUnicas.map(area => (
                    <MenuItem key={area} value={area} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={areasEjecucionFiltro.length === 0 || areasEjecucionFiltro.includes(area)} />
                      </ListItemIcon>
                      <ListItemText primary={area} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Jefe proyecto</InputLabel>
                <Select
                  multiple
                  value={jefesFiltro}
                  label="Jefe proyecto"
                  onChange={(e) => handleSelectChange(e, jefesUnicos, updateJefesFiltro, jefesFiltro)}
                  renderValue={() => getRenderValue(jefesFiltro, jefesUnicos, 'jefes')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(jefesFiltro, jefesUnicos)}
                        indeterminate={jefesFiltro.length > 0 && jefesFiltro.length < jefesUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {jefesUnicos.map(jefe => (
                    <MenuItem key={jefe} value={jefe} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={jefesFiltro.length === 0 || jefesFiltro.includes(jefe)} />
                      </ListItemIcon>
                      <ListItemText primary={jefe} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Línea de negocio</InputLabel>
                <Select
                  multiple
                  value={lineaNegocioFiltro}
                  label="Línea de negocio"
                  onChange={(e) => handleSelectChange(e, lineasNegocioUnicas, updateLineaNegocioFiltro, lineaNegocioFiltro)}
                  renderValue={() => getRenderValue(lineaNegocioFiltro, lineasNegocioUnicas, 'líneas')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(lineaNegocioFiltro, lineasNegocioUnicas)}
                        indeterminate={lineaNegocioFiltro.length > 0 && lineaNegocioFiltro.length < lineasNegocioUnicas.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todas" />
                  </MenuItem>
                  {lineasNegocioUnicas.map(linea => (
                    <MenuItem key={linea} value={linea} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={lineaNegocioFiltro.length === 0 || lineaNegocioFiltro.includes(linea)} />
                      </ListItemIcon>
                      <ListItemText primary={linea} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Negocio</InputLabel>
                <Select
                  multiple
                  value={isAllSelected(negocioFiltro, negociosUnicos) ? ['todos', ...negocioFiltro] : negocioFiltro}
                  label="Negocio"
                  onChange={(e) => handleSelectChange(e, negociosUnicos, updateNegocioFiltro, negocioFiltro)}
                  renderValue={() => getRenderValue(negocioFiltro, negociosUnicos, 'negocios')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem key="todos" value="todos" sx={{ fontSize: '0.85rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <input
                        type="checkbox"
                        checked={isAllSelected(negocioFiltro, negociosUnicos)}
                        onChange={() => {}}
                        style={{ marginRight: 8 }}
                      />
                      <strong>Todos</strong>
                    </Box>
                  </MenuItem>
                  {negociosUnicos.map(negocio => (
                    <MenuItem key={negocio} value={negocio} sx={{ fontSize: '0.85rem' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <input
                          type="checkbox"
                          checked={isAllSelected(negocioFiltro, negociosUnicos) || negocioFiltro.includes(negocio)}
                          onChange={() => {}}
                          style={{ marginRight: 8 }}
                        />
                        {negocio}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Subnegocio</InputLabel>
                <Select
                  multiple
                  value={negociosFiltro}
                  label="Subnegocio"
                  onChange={(e) => handleSelectChange(e, subNegociosUnicos, updateNegociosFiltro, negociosFiltro)}
                  renderValue={() => getRenderValue(negociosFiltro, subNegociosUnicos, 'subnegocios')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(negociosFiltro, subNegociosUnicos)}
                        indeterminate={negociosFiltro.length > 0 && negociosFiltro.length < subNegociosUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {subNegociosUnicos.map(subnegocio => (
                    <MenuItem key={subnegocio} value={subnegocio} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={negociosFiltro.length === 0 || negociosFiltro.includes(subnegocio)} />
                      </ListItemIcon>
                      <ListItemText primary={subnegocio} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtro de Innovación (múltiple) */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Innovación</InputLabel>
                <Select
                  multiple
                  value={innovacionFiltro}
                  label="Innovación"
                  onChange={(e) => handleSelectChange(e, ['innovacion', 'no_innovacion'], updateInnovacionFiltro, innovacionFiltro)}
                  renderValue={() => getRenderValue(innovacionFiltro, ['innovacion', 'no_innovacion'], 'opciones')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(innovacionFiltro, ['innovacion', 'no_innovacion'])}
                        indeterminate={innovacionFiltro.length > 0 && innovacionFiltro.length < 2}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  <MenuItem value="innovacion" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={innovacionFiltro.length === 0 || innovacionFiltro.includes('innovacion')} />
                    </ListItemIcon>
                    <ListItemText primary="Innovación" />
                  </MenuItem>
                  <MenuItem value="no_innovacion" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={innovacionFiltro.length === 0 || innovacionFiltro.includes('no_innovacion')} />
                    </ListItemIcon>
                    <ListItemText primary="No innovación" />
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Filtro de Tipo de Innovación (múltiple) */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Tipo Innovación</InputLabel>
                <Select
                  multiple
                  value={tipoInnovacionFiltro}
                  label="Tipo Innovación"
                  onChange={(e) => handleSelectChange(e, tiposInnovacionUnicos, updateTipoInnovacionFiltro, tipoInnovacionFiltro)}
                  renderValue={() => getRenderValue(tipoInnovacionFiltro, tiposInnovacionUnicos, 'tipos')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(tipoInnovacionFiltro, tiposInnovacionUnicos)}
                        indeterminate={tipoInnovacionFiltro.length > 0 && tipoInnovacionFiltro.length < tiposInnovacionUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {tiposInnovacionUnicos.map(tipo => (
                    <MenuItem key={tipo} value={tipo} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={tipoInnovacionFiltro.length === 0 || tipoInnovacionFiltro.includes(tipo)} />
                      </ListItemIcon>
                      <ListItemText primary={getTipoInnovacionDisplay(tipo)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtro de Tipo (múltiple) */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Tipo</InputLabel>
                <Select
                  multiple
                  value={tipoFiltro}
                  label="Tipo"
                  onChange={(e) => handleSelectChange(e, tiposUnicos, updateTipoFiltro, tipoFiltro)}
                  renderValue={() => getRenderValue(tipoFiltro, tiposUnicos, 'tipo')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(tipoFiltro, tiposUnicos)}
                        indeterminate={tipoFiltro.length > 0 && tipoFiltro.length < tiposUnicos.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {tiposUnicos.map(tipo => (
                    <MenuItem key={tipo} value={tipo} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={tipoFiltro.length === 0 || tipoFiltro.includes(tipo)} />
                      </ListItemIcon>
                      <ListItemText primary={getTipoDisplay(tipo)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtro de Madurez */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Madurez</InputLabel>
                <Select
                  multiple
                  value={madurezFiltro}
                  label="Madurez"
                  onChange={(e) => handleSelectChange(e, madurezsUnicas, updateMadurezFiltro, madurezFiltro)}
                  renderValue={() => getRenderValue(madurezFiltro, madurezsUnicas, 'madurez')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(madurezFiltro, madurezsUnicas)}
                        indeterminate={madurezFiltro.length > 0 && madurezFiltro.length < madurezsUnicas.length}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {madurezsUnicas.map(madurez => (
                    <MenuItem key={madurez} value={madurez} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={madurezFiltro.length === 0 || madurezFiltro.includes(madurez)} />
                      </ListItemIcon>
                      <ListItemText primary={getMadurezDisplay(madurez)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Filtro de Desvío */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Desvío</InputLabel>
                <Select
                  multiple
                  value={desvioFiltro}
                  label="Desvío"
                  onChange={(e) => handleSelectChange(e, ['sin_desvios', 'medio', 'grave'], updateDesvioFiltro, desvioFiltro)}
                  renderValue={() => getRenderValue(desvioFiltro, ['sin_desvios', 'medio', 'grave'], 'desvío')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(desvioFiltro, ['sin_desvios', 'medio', 'grave'])}
                        indeterminate={desvioFiltro.length > 0 && desvioFiltro.length < 3}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  <MenuItem value="sin_desvios" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('sin_desvios')} />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#2e7d32' }} />
                      <ListItemText primary="Sin desvíos" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="medio" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('medio')} />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#ff9800' }} />
                      <ListItemText primary="Desvío medio" />
                    </Box>
                  </MenuItem>
                  <MenuItem value="grave" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={desvioFiltro.length === 0 || desvioFiltro.includes('grave')} />
                    </ListItemIcon>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: '#d32f2f' }} />
                      <ListItemText primary="Desvío grave" />
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Botón Limpiar filtros */}
              <Button
                variant="contained"
                size="small"
                onClick={limpiarFiltros}
                sx={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)',
                  '&:hover': {
                    backgroundColor: '#c82333',
                    boxShadow: '0 4px 8px rgba(220, 53, 69, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <i className="fa-solid fa-filter-circle-xmark" style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                Limpiar filtros
              </Button>

              {/* Botón Recargar datos */}
              <Button
                variant="contained"
                size="small"
                onClick={recargarDatos}
                disabled={loadingIniciativas}
                sx={{
                  backgroundColor: hasValidCache ? '#6c757d' : '#007bff',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: hasValidCache ? '0 2px 4px rgba(108, 117, 125, 0.2)' : '0 2px 4px rgba(0, 123, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: hasValidCache ? '#5a6268' : '#0056b3',
                    boxShadow: hasValidCache ? '0 4px 8px rgba(108, 117, 125, 0.3)' : '0 4px 8px rgba(0, 123, 255, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  '&:disabled': {
                    backgroundColor: '#e9ecef',
                    color: '#6c757d'
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
                title={(() => {
                  const stats = getCacheStats();
                  return stats.hasCache
                    ? `Datos en caché desde: ${stats.lastFetched.toLocaleTimeString()}. Expira en ${stats.expiresIn} min.`
                    : 'Recargar datos desde el servidor';
                })()}
              >
                <i className={`fa-solid ${loadingIniciativas ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'}`} style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                {loadingIniciativas ? 'Cargando...' : (hasValidCache ? 'Actualizar' : 'Recargar')}
              </Button>

            </Box>

            {/* Pestañas y botón de nueva iniciativa en la misma fila */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2,
              borderBottom: 1,
              borderColor: 'divider'
            }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTabs-flexContainer': {
                    gap: 2
                  },
                  '& .MuiTabs-indicator': {
                    bottom: 0
                  },
                  flex: 1
                }}
              >
                <Tab
                  icon={<i className="fas fa-columns"></i>}
                  iconPosition="start"
                  label="Tablero"
                  sx={{
                    minHeight: 60,
                    gap: 1
                  }}
                />
                <Tab
                  icon={<i className="fas fa-table"></i>}
                  iconPosition="start"
                  label={`Lista de proyectos (${totalIniciativasFiltradas})`}
                  sx={{
                    minHeight: 60,
                    gap: 1
                  }}
                />
              </Tabs>
              
              <Button
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: '#212529',
                  color: '#fff',
                  borderColor: '#212529',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: '#495057',
                    borderColor: '#212529',
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    transform: 'translateY(-1px)',
                  },
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out',
                  mb: { xs: 0, sm: 1 }
                }}
                onClick={() => navigate('/crear-iniciativa')}
              >
                + Nuevo proyecto
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Contenido de las pestañas */}
        <TabPanel value={activeTab} index={0}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <KanbanOperaciones
              embedded={true}
              añosFiltro={añosSeleccionados}
              searchTerm={searchTerm}
              estadosFiltro={estadosFiltro}
              areasFiltro={areasFiltro}
              jefesFiltro={jefesFiltro}
              lineaNegocioFiltro={lineaNegocioFiltro}
              negocioFiltro={negocioFiltro}
              negociosFiltro={negociosFiltro}
              innovacionFiltro={innovacionFiltro}
              tipoInnovacionFiltro={tipoInnovacionFiltro}
              tipoFiltro={tipoFiltro}
              madurezFiltro={madurezFiltro}
              desvioFiltro={desvioFiltro}
              iniciativas={iniciativasFiltradas}
              loading={loadingIniciativas}
            />
          </Paper>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 2 }}>
            <TablaOperaciones
              embedded={true}
              añosFiltro={añosSeleccionados}
              searchTerm={searchTerm}
              estadosFiltro={estadosFiltro}
              areasFiltro={areasFiltro}
              jefesFiltro={jefesFiltro}
              lineaNegocioFiltro={lineaNegocioFiltro}
              negocioFiltro={negocioFiltro}
              negociosFiltro={negociosFiltro}
              innovacionFiltro={innovacionFiltro}
              tipoInnovacionFiltro={tipoInnovacionFiltro}
              tipoFiltro={tipoFiltro}
              madurezFiltro={madurezFiltro}
              desvioFiltro={desvioFiltro}
              iniciativas={iniciativasFiltradas}
              loading={loadingIniciativas}
            />
          </Paper>
        </TabPanel>

        {/* Footer con información adicional */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: '#6c757d' }}>
            💡 Tip: Usa el tablero para gestión visual de proyectos y la lista para análisis detallado
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Operaciones;